import { DocsLightTheme } from "@gadgetinc/widgets/src/DocsTheme";
import { ShopifyWebhookIcon } from "@gadgetinc/widgets/src/icons/ShopifyWebhookIcon";
import { ThirdPartyIcon } from "@gadgetinc/widgets/src/icons/ThirdPartyIcon";
import { GadgetLogoMark } from "@gadgetinc/widgets/src/icons/GadgetLogo";
import { OpenAIIcon } from "@gadgetinc/widgets/src/icons/OpenAIIcon";
import { useStyletron } from "baseui";
import { HeadingLarge, HeadingSmall, ParagraphLarge, ParagraphMedium, ParagraphSmall } from "baseui/typography";
import React from "react";
import type { DocsInterfaceProps } from "../src/chrome/DocsInterface";
import { DocsInterface } from "../src/chrome/DocsInterface";
import { DocLink } from "../src/markdown-components/DocLink";
import { SplashCard, SplashContentContainer, SplashHeading, colors } from "../src/components/SplashPage";

const heroLinks = [
  {
    href: "/guides",
    title: "Guides",
    description: "Concepts and tutorials that show you how to build an app in Gadget.",
    cta: "Browse guides",
    color: colors.blue,
  },
  {
    href: (appSlug: string) => `/api/${appSlug}/development`,
    title: "Generated API",
    description: "Detailed documentation on how to use your Gadget generated API.",
    cta: "Open your API docs",
    color: colors.purple,
  },
  {
    href: "/reference",
    title: "Reference",
    description: "An overview of the variety of packages and tools for your Gadget application.",
    cta: "View the reference",
    color: colors.grey,
  },
] as const;

const quickstarts = [
  {
    title: "Web or internal",
    body: "Create your first Gadget web app with built-in authentication and preview it in the editor.",
    copy: "Start building →",
    href: "/guides/getting-started/quickstarts/web-quickstart",
    icon: <GadgetLogoMark width={32} />,
  },
  {
    title: "Shopify",
    body: "Set up a Shopify connection and start building with Shopify data and webhooks.",
    copy: "Start with Shopify →",
    href: "/guides/plugins/shopify/quickstart",
    icon: <ShopifyWebhookIcon $size={32} />,
  },
  {
    title: "BigCommerce",
    body: "Set up a BigCommerce connection and start building with BigCommerce data and webhooks.",
    copy: "Start with BigCommerce →",
    href: "/guides/getting-started/quickstarts/bigcommerce-quickstart",
    icon: <ThirdPartyIcon type="bigcommerce" $size={32} />,
  },
  {
    title: "ChatGPT",
    body: "Build a ChatGPT app, including an MCP server and frontend, using built-in auth.",
    copy: "Start with ChatGPT →",
    href: "/guides/tutorials/chatgpt/todo-list",
    icon: <OpenAIIcon $size={32} />,
  },
] as const;

const basicsSections = [
  {
    title: "Get started",
    links: [
      { href: "/guides/getting-started/what-is-gadget", label: "What is Gadget?", description: "Platform overview" },
      { href: "/guides/getting-started/how-to-build-a-gadget-app", label: "How to build a Gadget app", description: "Core workflow" },
    ],
  },
  {
    title: "Framework concepts",
    links: [
      { href: "/guides/models/overview", label: "Models", description: "Postgres-backed data models" },
      { href: "/guides/actions/overview", label: "Actions", description: "Backend logic and queues" },
      { href: "/guides/data-access", label: "Data access", description: "Generated API and queries" },
      { href: "/guides/frontend", label: "Frontend", description: "UI and clients" },
    ],
  },
  {
    title: "Platform and tooling",
    links: [
      { href: "/guides/source-control", label: "Source control", description: "Local development" },
      { href: "/guides/development-tools/cli", label: "ggt, the Gadget CLI", description: "Command line tools" },
      { href: "/guides/environments", label: "Environments", description: "Dev and production" },
      { href: "/guides/plugins/sentry", label: "Sentry", description: "Error monitoring" },
    ],
  },
] as const;

const BasicsLink = (props: { href: string; label: string; description: string }) => {
  const [_css, $theme] = useStyletron();

  const titleLinkStyle = {
    display: "inline-block",
    textDecoration: "none",
    marginTop: 0,
    lineHeight: $theme.typography.ParagraphMedium.lineHeight,
  } as const;

  const descriptionStyle = {
    marginTop: $theme.sizing.scale100,
    marginBottom: 0,
    color: $theme.colors.contentSecondary,
    lineHeight: $theme.typography.ParagraphSmall.lineHeight,
  } as const;

  return (
    <div>
      <DocLink href={props.href} $style={titleLinkStyle}>
        <span data-docscontent="true" data-docsurl={props.href} data-docslinktext={props.label}>
          {props.label}
        </span>
      </DocLink>
      <ParagraphSmall $style={descriptionStyle}>{props.description}</ParagraphSmall>
    </div>
  );
};

const HomePage = (props: DocsInterfaceProps) => {
  const [css, $theme] = useStyletron();

  const sectionHeadingStyle = {
    marginTop: 0,
    marginBottom: $theme.sizing.scale300,
  } as const;

  const sectionIntroStyle = {
    marginTop: 0,
    marginBottom: $theme.sizing.scale500,
  } as const;

  const sectionContainerStyle = {
    paddingTop: $theme.sizing.scale800,
    paddingBottom: $theme.sizing.scale800,
    width: "100%",
    maxWidth: "1280px",
    marginLeft: "auto",
    marginRight: "auto",
    [DocsLightTheme.mediaQuery.xsmall]: {
      paddingTop: $theme.sizing.scale800,
      paddingBottom: $theme.sizing.scale800,
    },
    [DocsLightTheme.mediaQuery.medium]: {
      paddingTop: $theme.sizing.scale800,
      paddingBottom: $theme.sizing.scale800,
    },
  } as const;

  const basicsLinksStyle = {
    display: "grid",
    rowGap: $theme.sizing.scale700,
    columnGap: $theme.sizing.scale700,
    gridTemplateColumns: "1fr",
    marginTop: 0,
    width: "100%",
    [DocsLightTheme.mediaQuery.xsmall]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
    [DocsLightTheme.mediaQuery.medium]: {
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    },
  } as const;

  const linkListResetStyle = {
    listStyle: "none",
    marginTop: 0,
    marginBottom: 0,
    paddingLeft: 0,
    width: "100%",
  } as const;

  const linkListItemStyle = {
    marginTop: 0,
    marginBottom: 0,
  } as const;

  const basicsColumnHeadingStyle = {
    marginTop: 0,
    marginBottom: $theme.sizing.scale200,
  } as const;

  const basicsColumnListStyle = {
    display: "grid",
    rowGap: $theme.sizing.scale500,
    listStyle: "none",
    marginTop: 0,
    marginBottom: 0,
    paddingLeft: 0,
  } as const;

  const quickstartsGridStyle = {
    display: "grid",
    gap: $theme.sizing.scale600,
    gridTemplateColumns: "1fr",
    [DocsLightTheme.mediaQuery.xsmall]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
    [DocsLightTheme.mediaQuery.medium]: {
      gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    },
  } as const;

  const heroLayoutStyle = {
    display: "grid",
    gap: $theme.sizing.scale700,
    alignItems: "start",
    [DocsLightTheme.mediaQuery.medium]: {
      gridTemplateColumns: "minmax(0, 1.4fr) minmax(320px, 0.9fr)",
      gap: $theme.sizing.scale900,
    },
  } as const;

  const heroCardsStackStyle = {
    display: "grid",
    gap: $theme.sizing.scale400,
    width: "100%",
  } as const;

  const compactHeroCardStyle = (color: (typeof colors)[keyof typeof colors]) =>
    ({
      display: "grid",
      gap: $theme.sizing.scale300,
      color: color.content,
      backgroundColor: color.background,
      borderColor: color.border,
      borderRadius: $theme.sizing.scale300,
      borderWidth: "1px",
      borderStyle: "solid",
      boxShadow: `0px 4px 16px 0px ${color.boxShadow}`,
      paddingTop: $theme.sizing.scale500,
      paddingBottom: $theme.sizing.scale500,
      paddingLeft: $theme.sizing.scale600,
      paddingRight: $theme.sizing.scale600,
      textDecoration: "none",
      [":hover"]: {
        boxShadow: `0px 6px 16px 0px ${color.boxShadow}`,
        filter: "brightness(0.97)",
      },
    }) as const;

  const compactHeroCardTextStyle = {
    marginTop: 0,
    marginBottom: 0,
    color: "inherit",
    lineHeight: $theme.typography.ParagraphMedium.lineHeight,
  } as const;

  const compactHeroCardCtaStyle = {
    marginTop: 0,
    marginBottom: 0,
    color: "inherit",
    lineHeight: $theme.typography.ParagraphSmall.lineHeight,
  } as const;

  return (
    <DocsInterface
      {...props}
      showLeftSideNav={false}
      fullWidthFooter={true}
      footerMaxWidth="1280px"
      footerPaddingX={$theme.sizing.scale600}
    >
      <div
        className={css({
          backgroundColor: "#f9f9f9",
        })}
      >
        <SplashContentContainer
          $style={{
            paddingTop: $theme.sizing.scale700,
            paddingBottom: $theme.sizing.scale500,
            [DocsLightTheme.mediaQuery.xsmall]: {
              paddingTop: $theme.sizing.scale1200,
              paddingBottom: $theme.sizing.scale700,
            },
            [DocsLightTheme.mediaQuery.medium]: {
              paddingTop: "72px",
              paddingBottom: $theme.sizing.scale700,
            },
          }}
        >
          <div className={css(heroLayoutStyle)}>
            <div>
              <SplashHeading data-docscontent="true">Gadget developer documentation</SplashHeading>
              <ParagraphLarge
                data-docscontent="true"
                $style={{
                  paddingTop: $theme.sizing.scale400,
                  maxWidth: "680px",
                  marginBottom: 0,
                }}
              >
                Gadget is an infraless development and hosting platform for building full-stack apps. We provide a pre-stitched stack for
                data, backend logic, APIs, frontend development, and hosting so you can focus on the code that is unique to your app.
              </ParagraphLarge>
            </div>
            <div className={css(heroCardsStackStyle)}>
              {heroLinks.map((link) => (
                <DocLink
                  key={link.title}
                  href={typeof link.href === "function" ? link.href(props.currentApp.slug) : link.href}
                  $style={compactHeroCardStyle(link.color)}
                >
                  <div
                    data-docscontent="true"
                    data-docsurl={typeof link.href === "function" ? link.href(props.currentApp.slug) : link.href}
                    data-docslinktext={`${link.cta} →`}
                  >
                    <ParagraphMedium $style={compactHeroCardTextStyle}>
                      <strong>{link.title}:</strong> {link.description}
                    </ParagraphMedium>
                    <ParagraphSmall $style={compactHeroCardCtaStyle}>
                      <strong>{link.cta} →</strong>
                    </ParagraphSmall>
                  </div>
                </DocLink>
              ))}
            </div>
          </div>
        </SplashContentContainer>
      </div>

      <SplashContentContainer $style={sectionContainerStyle}>
        <HeadingLarge data-docscontent="true" $style={sectionHeadingStyle}>
          Quickstarts
        </HeadingLarge>
        <ParagraphMedium data-docscontent="true" $style={{ ...sectionIntroStyle, maxWidth: "680px" }}>
          Pick the fastest path to a working app based on what you are building.
        </ParagraphMedium>
        <div
          className={css({
            ...quickstartsGridStyle,
            marginTop: $theme.sizing.scale500,
            marginBottom: $theme.sizing.scale500,
          })}
        >
          {quickstarts.map((quickstart) => (
            <SplashCard key={quickstart.title} {...quickstart} color={colors.white} />
          ))}
        </div>
        <DocLink
          href="/guides/tutorials"
          $style={{
            marginTop: $theme.sizing.scale800,
            display: "inline-block",
            textDecoration: "none",
            lineHeight: $theme.typography.ParagraphMedium.lineHeight,
          }}
        >
          <span data-docscontent="true" data-docsurl="/guides/tutorials" data-docslinktext="See more tutorials →">
            See more tutorials →
          </span>
        </DocLink>
      </SplashContentContainer>

      <SplashContentContainer $style={sectionContainerStyle}>
        <HeadingLarge data-docscontent="true" $style={sectionHeadingStyle}>
          Learn the basics
        </HeadingLarge>
        <ParagraphMedium data-docscontent="true" $style={sectionIntroStyle}>
          Once you have a quickstart running, use these guides to understand how Gadget fits together.
        </ParagraphMedium>
        <ul
          className={css({
            ...basicsLinksStyle,
            ...linkListResetStyle,
            marginTop: $theme.sizing.scale500,
          })}
        >
          {basicsSections.map((section) => (
            <li key={section.title} className={css(linkListItemStyle)}>
              <HeadingSmall data-docscontent="true" $style={basicsColumnHeadingStyle}>
                {section.title}
              </HeadingSmall>
              <ul className={css(basicsColumnListStyle)}>
                {section.links.map((link) => (
                  <li key={link.href} className={css(linkListItemStyle)}>
                    <BasicsLink href={link.href} label={link.label} description={link.description} />
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </SplashContentContainer>
    </DocsInterface>
  );
};

export default HomePage;
